import * as React from "react";
import { Box } from "@mui/material";

type DemoCaptionAnchor =
  | "top-left"
  | "top-center"
  | "top-right"
  | "middle-left"
  | "middle-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

type DemoStepDetail = {
  eyebrow?: string;
  title?: string;
  body?: string;
  anchor?: DemoCaptionAnchor;
  highlight?: DemoHighlightRect | null;
  hidden?: boolean;
  variant?: "default" | "outro" | "fadeout";
  showCaptureNote?: boolean;
};

type DemoStepState = Required<DemoStepDetail> & {
  nonce: number;
};

type DemoHighlightRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  label?: string;
};

type DemoActionDetail = {
  kind?: "click" | "hover" | "drag";
  label?: string;
  x?: number;
  y?: number;
  x2?: number;
  y2?: number;
  ttlMs?: number;
  rect?: DemoHighlightRect | null;
};

type DemoActionState = DemoActionDetail & {
  kind: "click" | "hover" | "drag";
  x: number;
  y: number;
  nonce: number;
};

const DEFAULT_STEP: DemoStepState = {
  eyebrow: "Game Story demo",
  title: "Pickleball Game Explorer",
  body: "A guided pass through rally context, video review, bookmarks, reasons, and AI-assisted review.",
  anchor: "bottom-left",
  highlight: null,
  hidden: false,
  variant: "default",
  showCaptureNote: true,
  nonce: 0,
};

function demoRunEnabled() {
  try {
    return new URLSearchParams(window.location.search).get("demoRun") === "1";
  } catch {
    return false;
  }
}

function normalizeAnchor(_anchor: DemoStepDetail["anchor"]): DemoCaptionAnchor {
  return "bottom-left";
}

function anchorSx(anchor: DemoCaptionAnchor) {
  switch (anchor) {
    case "top-left":
      return { top: 28, left: 28 };
    case "top-right":
      return { top: 28, right: 28 };
    case "middle-left":
      return { top: "50%", left: 28, transform: "translateY(-50%)" };
    case "middle-right":
      return { top: "50%", right: 28, transform: "translateY(-50%)" };
    case "bottom-left":
      return { bottom: 30, left: 28 };
    case "bottom-center":
      return { bottom: 30, left: "50%", transform: "translateX(-50%)" };
    case "bottom-right":
      return { bottom: 30, right: 28 };
    case "top-center":
    default:
      return { top: 26, left: "50%", transform: "translateX(-50%)" };
  }
}

function normalizeHighlight(value: unknown): DemoHighlightRect | null {
  const rect = value as Partial<DemoHighlightRect> | null | undefined;
  if (
    !rect ||
    !Number.isFinite(rect.x) ||
    !Number.isFinite(rect.y) ||
    !Number.isFinite(rect.width) ||
    !Number.isFinite(rect.height) ||
    Number(rect.width) < 8 ||
    Number(rect.height) < 8
  ) {
    return null;
  }
  return {
    x: Number(rect.x),
    y: Number(rect.y),
    width: Number(rect.width),
    height: Number(rect.height),
    label: typeof rect.label === "string" ? rect.label : undefined,
  };
}

function normalizeAction(value: unknown, nonce: number): DemoActionState | null {
  const action = value as DemoActionDetail | null | undefined;
  if (!action) return null;
  const kind = action?.kind ?? "click";
  const ttlMs = Number(action.ttlMs);
  if (
    !["click", "hover", "drag"].includes(kind) ||
    !Number.isFinite(action?.x) ||
    !Number.isFinite(action?.y)
  ) {
    return null;
  }
  return {
    ...action,
    kind,
    x: Number(action.x),
    y: Number(action.y),
    x2: Number.isFinite(action.x2) ? Number(action.x2) : undefined,
    y2: Number.isFinite(action.y2) ? Number(action.y2) : undefined,
    ttlMs: Number.isFinite(ttlMs)
      ? Math.max(250, Math.min(8000, ttlMs))
      : undefined,
    rect: normalizeHighlight(action.rect),
    nonce,
  };
}

function HighlightBox({
  rect,
  tone,
}: {
  rect: DemoHighlightRect;
  tone: "caption" | "action";
}) {
  const pad = tone === "caption" ? 8 : 6;
  const color = tone === "caption" ? "#38bdf8" : "#facc15";
  const bg =
    tone === "caption" ? "rgba(56,189,248,0.08)" : "rgba(250,204,21,0.1)";
  return (
    <Box
      sx={{
        position: "fixed",
        left: Math.max(0, rect.x - pad),
        top: Math.max(0, rect.y - pad),
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
        border: `3px solid ${color}`,
        borderRadius: 2,
        bgcolor: bg,
        boxShadow: `0 0 0 3px rgba(0,0,0,0.42), 0 0 30px ${color}`,
        animation:
          tone === "caption"
            ? "demoTargetGlow 1600ms ease-out 1"
            : "demoActionRectGlow 1200ms ease-out 1",
      }}
    >
      {rect.label ? (
        <Box
          sx={{
            position: "absolute",
            top: -31,
            left: 0,
            maxWidth: 280,
            px: 1,
            py: 0.35,
            borderRadius: 1,
            bgcolor: tone === "caption" ? "rgba(8,47,73,0.94)" : "rgba(82,59,0,0.94)",
            color: "#fff",
            fontSize: 12,
            fontWeight: 900,
            lineHeight: 1.1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
          }}
        >
          {tone === "caption" ? "Focus: " : ""}
          {rect.label}
        </Box>
      ) : null}
    </Box>
  );
}

function ActionMarker({ action }: { action: DemoActionState }) {
  const color =
    action.kind === "hover" ? "#facc15" : action.kind === "drag" ? "#fb923c" : "#fde047";
  const label =
    action.label || (action.kind === "hover" ? "Hover" : action.kind === "drag" ? "Drag" : "Click");
  const hasLine =
    action.kind === "drag" &&
    Number.isFinite(action.x2) &&
    Number.isFinite(action.y2);
  const dx = hasLine ? Number(action.x2) - action.x : 0;
  const dy = hasLine ? Number(action.y2) - action.y : 0;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);

  return (
    <>
      {hasLine ? (
        <Box
          sx={{
            position: "fixed",
            left: action.x,
            top: action.y,
            width: distance,
            height: 4,
            bgcolor: color,
            borderRadius: 99,
            boxShadow: `0 0 18px ${color}, 0 0 0 2px rgba(0,0,0,0.38)`,
            transform: `rotate(${angle}deg)`,
            transformOrigin: "0 50%",
            animation: "demoActionLine 1400ms ease-out 1",
          }}
        />
      ) : null}
      <Box
        key={action.nonce}
        sx={{
          position: "fixed",
          left: action.x,
          top: action.y,
          transform: "translate(-50%, -50%)",
          width: action.kind === "hover" ? 62 : 74,
          height: action.kind === "hover" ? 62 : 74,
          borderRadius: "50%",
          border:
            action.kind === "hover"
              ? `3px dashed ${color}`
              : `4px solid ${color}`,
          bgcolor:
            action.kind === "hover"
              ? "rgba(250,204,21,0.1)"
              : "rgba(253,224,71,0.12)",
          boxShadow: `0 0 0 4px rgba(0,0,0,0.4), 0 0 34px ${color}`,
          animation:
            action.kind === "hover"
              ? "demoHoverPulse 1800ms ease-out infinite"
              : "demoClickPulse 1800ms ease-out infinite",
        }}
      />
      <Box
        sx={{
          position: "fixed",
          left: Math.min(window.innerWidth - 300, action.x + 42),
          top: Math.max(10, action.y - 18),
          maxWidth: 280,
          px: 1.1,
          py: 0.55,
          borderRadius: 1,
          bgcolor: "rgba(20,20,22,0.92)",
          border: `1px solid ${color}`,
          color: "#fff",
          fontSize: 13,
          fontWeight: 950,
          lineHeight: 1.14,
          boxShadow: "0 14px 34px rgba(0,0,0,0.42)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {action.kind === "hover" ? "Hover" : action.kind === "drag" ? "Drag" : "Click"}: {label}
      </Box>
    </>
  );
}

export function DemoRunOverlay() {
  const [enabled] = React.useState(demoRunEnabled);
  const [step, setStep] = React.useState<DemoStepState>(DEFAULT_STEP);
  const [action, setAction] = React.useState<DemoActionState | null>(null);
  const actionNonceRef = React.useRef(0);
  const clearActionRef = React.useRef<number | null>(null);

  function clearCurrentAction() {
    if (clearActionRef.current != null) {
      window.clearTimeout(clearActionRef.current);
      clearActionRef.current = null;
    }
    setAction(null);
  }

  React.useEffect(() => {
    if (!enabled) return;

    function onStep(event: Event) {
      const detail = (event as CustomEvent<DemoStepDetail>).detail ?? {};
      clearCurrentAction();
      if (detail.hidden) {
        setStep((current) => ({
          eyebrow: "",
          title: "",
          body: "",
          anchor: "bottom-left",
          highlight: null,
          hidden: true,
          variant: "default",
          showCaptureNote: true,
          nonce: current.nonce + 1,
        }));
        return;
      }
      setStep((current) => ({
        eyebrow: detail.eyebrow ?? DEFAULT_STEP.eyebrow,
        title: detail.title ?? DEFAULT_STEP.title,
        body: detail.body ?? DEFAULT_STEP.body,
        anchor: normalizeAnchor(detail.anchor),
        highlight: normalizeHighlight(detail.highlight),
        hidden: false,
        variant:
          detail.variant === "outro"
            ? "outro"
            : detail.variant === "fadeout"
              ? "fadeout"
              : "default",
        showCaptureNote: detail.showCaptureNote !== false,
        nonce: current.nonce + 1,
      }));
    }

    window.addEventListener("pbv-demo-step", onStep as EventListener);
    return () =>
      window.removeEventListener("pbv-demo-step", onStep as EventListener);
  }, [enabled]);

  React.useEffect(() => {
    if (!enabled) return;

    function onAction(event: Event) {
      actionNonceRef.current += 1;
      const next = normalizeAction(
        (event as CustomEvent<DemoActionDetail>).detail,
        actionNonceRef.current,
      );
      if (!next) return;
      setAction(next);
      if (clearActionRef.current != null) {
        window.clearTimeout(clearActionRef.current);
      }
      const ttl =
        next.ttlMs ??
        (next.kind === "hover" ? 6800 : next.kind === "drag" ? 5200 : 650);
      clearActionRef.current = window.setTimeout(() => {
        setAction(null);
        clearActionRef.current = null;
      }, ttl);
    }

    function onActionClear() {
      clearCurrentAction();
    }

    window.addEventListener("pbv-demo-action", onAction as EventListener);
    window.addEventListener("pbv-demo-action-clear", onActionClear);
    return () => {
      window.removeEventListener("pbv-demo-action", onAction as EventListener);
      window.removeEventListener("pbv-demo-action-clear", onActionClear);
      if (clearActionRef.current != null) window.clearTimeout(clearActionRef.current);
    };
  }, [enabled]);

  if (!enabled) return null;
  const hideCaptureNote =
    typeof document !== "undefined" &&
    document.body?.dataset.pbvDemoHideCaptureNote === "true";

  return (
    <>
      <Box
        data-demo="demo-run-screen-effects"
        sx={{
          "@keyframes demoTargetGlow": {
            "0%": {
              opacity: 0,
              transform: "scale(0.985)",
            },
            "18%": { opacity: 1, transform: "scale(1.01)" },
            "100%": { opacity: 1, transform: "scale(1)" },
          },
          "@keyframes demoActionRectGlow": {
            "0%": { opacity: 0, transform: "scale(0.98)" },
            "20%": { opacity: 1, transform: "scale(1.015)" },
            "100%": { opacity: 1, transform: "scale(1)" },
          },
          "@keyframes demoClickPulse": {
            "0%": { opacity: 1, transform: "translate(-50%, -50%) scale(0.72)" },
            "70%": { opacity: 0.72, transform: "translate(-50%, -50%) scale(1.18)" },
            "100%": { opacity: 1, transform: "translate(-50%, -50%) scale(0.86)" },
          },
          "@keyframes demoHoverPulse": {
            "0%": { opacity: 0.72, transform: "translate(-50%, -50%) scale(0.9) rotate(0deg)" },
            "50%": { opacity: 1, transform: "translate(-50%, -50%) scale(1.08) rotate(8deg)" },
            "100%": { opacity: 0.72, transform: "translate(-50%, -50%) scale(0.9) rotate(0deg)" },
          },
          "@keyframes demoActionLine": {
            "0%": { opacity: 0 },
            "35%": { opacity: 1 },
            "100%": { opacity: 1 },
          },
          position: "fixed",
          inset: 0,
          zIndex: 19990,
          pointerEvents: "none",
        }}
      >
        {step.highlight ? <HighlightBox rect={step.highlight} tone="caption" /> : null}
        {action?.rect ? <HighlightBox rect={action.rect} tone="action" /> : null}
        {action ? <ActionMarker action={action} /> : null}
      </Box>
      {step.variant !== "outro" && step.showCaptureNote && !hideCaptureNote ? (
        <Box
          data-demo="demo-run-capture-note"
          sx={{
            position: "fixed",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 19998,
            maxWidth: "min(420px, calc(100vw - 28px))",
            px: 1.2,
            py: 0.65,
            borderRadius: 1.5,
            bgcolor: "rgba(8,12,18,0.78)",
            border: "1px solid rgba(248,113,113,0.58)",
            color: "rgba(255,255,255,0.86)",
            boxShadow:
              "0 10px 34px rgba(0,0,0,0.34), 0 0 18px rgba(248,113,113,0.12)",
            backdropFilter: "blur(10px)",
            fontSize: 12.5,
            fontWeight: 800,
            lineHeight: 1.25,
            pointerEvents: "none",
          }}
        >
          Demo video may look slower or choppier here; live app playback is normal.
        </Box>
      ) : null}
      {!step.hidden ? (
        <Box
          key={step.nonce}
          data-demo="demo-run-overlay"
          sx={{
        ...(step.variant === "fadeout"
          ? {
              "@keyframes demoFadeToBlack": {
                "0%": { backgroundColor: "rgba(5,9,12,0.96)" },
                "100%": { backgroundColor: "#000000" },
              },
              "@keyframes demoOutroTextFade": {
                "0%": { opacity: 1 },
                "100%": { opacity: 0 },
              },
              position: "fixed",
              inset: 0,
              zIndex: 20001,
              width: "auto",
              minHeight: "100vh",
              pointerEvents: "none",
              border: 0,
              borderRadius: 0,
              bgcolor: "rgba(5,9,12,0.96)",
              color: "#ffffff",
              boxShadow: "none",
              animation: "demoFadeToBlack 1200ms ease-in forwards",
              px: "min(9vw, 96px)",
              py: "min(9vh, 96px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              overflow: "hidden",
              "& [data-demo='demo-run-eyebrow']": {
                color: "#8dd8ff",
                fontSize: 13,
                letterSpacing: 1.8,
                mb: 1.2,
                animation: "demoOutroTextFade 1000ms ease-in forwards",
              },
              "& [data-demo='demo-run-title']": {
                fontSize: "clamp(34px, 5vw, 64px)",
                maxWidth: 980,
                lineHeight: 0.98,
                mb: 2,
                animation: "demoOutroTextFade 1000ms ease-in forwards",
              },
              "& [data-demo='demo-run-body']": {
                fontSize: "clamp(17px, 1.55vw, 24px)",
                maxWidth: 760,
                lineHeight: 1.42,
                color: "rgba(255,255,255,0.78)",
                animation: "demoOutroTextFade 1000ms ease-in forwards",
              },
            }
          : step.variant === "outro"
          ? {
              position: "fixed",
              inset: 0,
              zIndex: 20000,
              width: "auto",
              minHeight: "100vh",
              pointerEvents: "none",
              border: 0,
              borderRadius: 0,
              bgcolor: "rgba(5,9,12,0.96)",
              color: "#ffffff",
              boxShadow: "none",
              backdropFilter: "none",
              px: "min(9vw, 96px)",
              py: "min(9vh, 96px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              animation: "demoCaptionPulse 1200ms ease-out 1",
              overflow: "hidden",
              "&::before": { display: "none" },
              "& [data-demo='demo-run-eyebrow']": {
                color: "#8dd8ff",
                fontSize: 13,
                letterSpacing: 1.8,
                mb: 1.2,
              },
              "& [data-demo='demo-run-title']": {
                fontSize: "clamp(34px, 5vw, 64px)",
                maxWidth: 980,
                lineHeight: 0.98,
                mb: 2,
              },
              "& [data-demo='demo-run-body']": {
                fontSize: "clamp(17px, 1.55vw, 24px)",
                maxWidth: 760,
                lineHeight: 1.42,
                color: "rgba(255,255,255,0.78)",
              },
            }
          : {
        "@keyframes demoCaptionPulse": {
          "0%": {
            borderColor: "rgba(124,219,255,0.92)",
            boxShadow:
              "0 0 0 0 rgba(56,189,248,0.45), 0 18px 64px rgba(0,0,0,0.46)",
          },
          "55%": {
            borderColor: "rgba(255,255,255,0.32)",
            boxShadow:
              "0 0 0 12px rgba(56,189,248,0), 0 18px 64px rgba(0,0,0,0.46)",
          },
          "100%": {
            borderColor: "rgba(255,255,255,0.22)",
            boxShadow:
              "0 0 0 0 rgba(56,189,248,0), 0 18px 64px rgba(0,0,0,0.46)",
          },
        },
        "@keyframes demoCaptionAccent": {
          "0%": { opacity: 1, transform: "scaleY(1)" },
          "100%": { opacity: 0.62, transform: "scaleY(0.62)" },
        },
        position: "fixed",
        zIndex: 20000,
        width: "min(560px, calc(100vw - 36px))",
        pointerEvents: "none",
        border: "1px solid rgba(255,255,255,0.22)",
        borderRadius: 2,
        bgcolor: "rgba(10,12,16,0.86)",
        color: "#ffffff",
        boxShadow: "0 18px 64px rgba(0,0,0,0.46)",
        backdropFilter: "blur(12px)",
        px: 2,
        py: 1.6,
        animation: "demoCaptionPulse 1200ms ease-out 1",
        overflow: "hidden",
        ...anchorSx(step.anchor),
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            bgcolor: "#38bdf8",
            boxShadow: "0 0 22px rgba(56,189,248,0.78)",
            transformOrigin: "50% 50%",
            animation: "demoCaptionAccent 1400ms ease-out 1",
          },
        })}}
      >
        <Box
          data-demo="demo-run-eyebrow"
          sx={{
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            color: "#8dd8ff",
            mb: 0.6,
          }}
        >
          {step.eyebrow}
        </Box>
        <Box data-demo="demo-run-title" sx={{ fontSize: 22, fontWeight: 950, lineHeight: 1.08, mb: 0.8 }}>
          {step.title}
        </Box>
        <Box data-demo="demo-run-body" sx={{ fontSize: 14.5, lineHeight: 1.42, color: "rgba(255,255,255,0.84)" }}>
          {step.body}
        </Box>
      </Box>
      ) : null}
    </>
  );
}
